import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../config/app-settings'
import {
  useGetTaskDistributionsQuery,
  useDeleteTaskDistributionMutation
} from '../../redux/apis/taskManagement/tMDistriputionApiSlice'
import ReusableTable from '../../components/other/ReuseableTable'
import Loader from '../pages/loader'
import SearchAndFilterBar from '../../components/other/search/SearchAndFilterBar'
import { toast } from 'react-toastify'
import AssignTask from '../../components/OpsPerformance/AssignTask'
import UpdateWeights from '../../components/OpsPerformance/UpdateWeights'
import DistributeTasks from '../../components/OpsPerformance/DistributeTasks'
import AssigneeDropdown from '../../components/AssigneeDropdown'
import CampaignDropdown from '../../components/CampaignDropdown'
import { Modal } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { can } from '../../helpers/auth'

const DistributionTasksPage = React.memo(() => {
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({})
  const context = useContext(AppSettings)

  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 })
  const [assignTaskOpen, setAssignTaskOpen] = useState(false)
  const [updateWeightsOpen, setUpdateWeightsOpen] = useState(false)
  const [distributeTasksOpen, setDistributeTasksOpen] = useState(false)

  const [
    deleteTaskDistribution,
    { isLoading: isDeleting, isSuccess: deleteSuccess, error: deleteError }
  ] = useDeleteTaskDistributionMutation()

  // GET /api/v1/task-distribution/
  const {
    data: taskDistributions,
    isLoading: isLoadingDistributions,
    error: distributionsError,
    isFetching
  } = useGetTaskDistributionsQuery({
    expand: ['user', 'campaign'].join(','),
    page: pagination.current,
    page_size: pagination.pageSize,
    ...(search ? { search } : {}),
    ...filters
  })

  const handleDelete = record => {
    Modal.confirm({
      title: 'Delete Task Distribution',
      icon: <ExclamationCircleOutlined />,
      content: `Are you sure you want to delete task distribution #${record.id}?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await deleteTaskDistribution(record.id).unwrap()
          toast.success('Task distribution deleted successfully!')
        } catch (err) {
          console.error(err)
        }
      }
    })
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true },
    {
      title: 'Assignee',
      dataIndex: 'user',
      key: 'user',
      filter: true,
      filterComponent: AssigneeDropdown,
      render: user => (user ? `${user.first_name}  ${user.last_name}` : '')
    },
    {
      title: 'CAMPAIGN',
      dataIndex: 'campaign',
      key: 'campaign',
      filter: true,
      filterComponent: CampaignDropdown,
      render: campaign => (campaign ? campaign.campaign_name : '')
    },
    {
      title: 'CREATED AT',
      dataIndex: 'created_at',
      key: 'created_at',
      // filter: true,
      // filterType: 'date',
      hidden: true
    },
    {
      title: 'UPDATED AT',
      dataIndex: 'updated_at',
      key: 'updated_at',
      // filter: true,
      // filterType: 'date',
      hidden: true
    },

    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'actions',
      render: (text, record) => (
        <div className='flex gap-2'>
          {can('delete_task_distribution') && (
            <button
              className='btn btn-outline-danger'
              onClick={() => handleDelete(record)}
            >
              Delete
            </button>
          )}
        </div>
      )
    }
  ]

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  useEffect(() => {
    if (distributionsError?.data) {
      const errorMessages = Object.values(distributionsError.data).flat()
      errorMessages.forEach(msg => toast.error(msg))
    }
  }, [distributionsError])

  useEffect(() => {
    if (deleteError?.data) {
      const errorMessages = Object.values(deleteError.data).flat()
      errorMessages.forEach(msg => toast.error(msg))
    }
  }, [deleteError])

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoadingDistributions || isFetching || isDeleting} />

      {can('post_task_distribution') && assignTaskOpen && (
        <AssignTask open={assignTaskOpen} setOpen={setAssignTaskOpen} />
      )}

      {can('put_user_weights') && updateWeightsOpen && (
        <UpdateWeights
          open={updateWeightsOpen}
          setOpen={setUpdateWeightsOpen}
        />
      )}

      {can('Distribute_Tasks_ops') && distributeTasksOpen && (
        <DistributeTasks
          open={distributeTasksOpen}
          setOpen={setDistributeTasksOpen}
        />
      )}

      <div className='flex'>
        <SearchAndFilterBar
          columns={columns}
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          setPagination={setPagination}
        />

        <div className='ml-auto flex gap-4'>
          {can('post_task_distribution') && (
            <button
              className='btn btn-outline-theme mb-4'
              onClick={() => setAssignTaskOpen(true)}
            >
              Assign Task
            </button>
          )}
          {can('put_user_weights') && (
            <button
              className='btn btn-outline-theme mb-4'
              onClick={() => setUpdateWeightsOpen(true)}
            >
              Update Weights
            </button>
          )}
          {can('Distribute_Tasks_ops') && (
            <button
              className='btn btn-primary mb-4'
              onClick={() => setDistributeTasksOpen(true)}
            >
              Distribute Tasks
            </button>
          )}
        </div>
      </div>

      <div className='row'>
        <ReusableTable
          columns={columns}
          data={taskDistributions?.data?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: taskDistributions?.data?.count,
            showSizeChanger: true
          }}
          onChange={handleTableChange}
        />
      </div>
    </div>
  )
})

export default DistributionTasksPage
